import { useCallback, useEffect, useMemo, useState } from 'react';

import { AxiosResponse } from 'axios';

import { RiskEntitiesFilter, RiskEntity } from '../../types/RiskEntities';
import { HTTP_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { Pageable } from '../../../../../api/types/Pageable';
import { httpGet } from '../../../../../components/utils';

const ENTITIES_PAGE_SIZE = 100;

const useGetRiskEntities = ({
    entityName,
    riskSeverity,
    entityType,
    riskIndicators,
    domains,
    orderByField,
    orderByDirection
}: Omit<RiskEntitiesFilter, 'pageSize' | 'pageNumber'>) => {
    const [data, setData] = useState<Pageable<RiskEntity[]>>();
    const [isFetching, setIsFetching] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [error, setError] = useState<string>();

    const entitiesFilters = useMemo(() => {
        return {
            entityName,
            riskSeverity,
            domains,
            entityType,
            riskIndicators,
            orderByField,
            orderByDirection
        };
    }, [entityName, riskSeverity, domains, entityType, riskIndicators, orderByField, orderByDirection]);

    const fetchEntitiesPage = useCallback(async (filters: RiskEntitiesFilter) => {
        setIsFetching(true);
        setError(undefined);
        setHasNextPage(true);

        try {
            const response = (await httpGet('/risk-entities', {
                [HTTP_OPTIONS.REQ_OPTIONS]: {
                    params: filters
                }
            })) as unknown as AxiosResponse<RiskEntity[]>;

            const page = response.data;

            if (!page?.length) {
                setHasNextPage(false);
            } else {
                setData((prevState) => {
                    return prevState
                        ? { pages: [...prevState.pages, page] }
                        : {
                              pages: [page]
                          };
                });
            }
        } catch (err) {
            setError('Failed to fetch risk entities');
        }

        setIsFetching(false);
    }, []);

    const fetchNextPage = useCallback(() => {
        if (data && data.pages.length && hasNextPage) {
            const nextPage = data.pages.length + 1;
            fetchEntitiesPage({ ...entitiesFilters, pageNumber: nextPage, pageSize: ENTITIES_PAGE_SIZE });
        }
    }, [data, entitiesFilters, fetchEntitiesPage, hasNextPage]);

    useEffect(() => {
        setData(undefined);
        fetchEntitiesPage(entitiesFilters);
    }, [entitiesFilters, fetchEntitiesPage]);

    return { data, isFetching, error, fetchNextPage, hasNextPage };
};

export default useGetRiskEntities;
